import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { FormProvider, useForm } from "react-hook-form"
import { store } from "react-notifications-component"
import { Route, useHistory, useRouteMatch } from "react-router"
import { notificationOptions } from "../../../api/Definitions"
import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import { iAccess } from "../../../definitions/Access"
import { iArtifact } from "../../../definitions/Artifacts"
import {
  iInspectorProps,
  iResponse,
  iUrlParams,
} from "../../../definitions/interfaces"
import { ItemWithAccess } from "../../../definitions/types"
import { errorNotification } from "../../../helpers/notificationFunctions"
import {
  get,
  handleDeleteData,
  handleUpdateData,
  post,
} from "../../../store/data/actions"
import ArtifactHeader from "../Header"
import ArtifactDetails from "./Details"
import ArtifactOverview from "./Overview"
import ArtifactUploads from "./Uploads"

const ArtifactInspector: FunctionComponent<iInspectorProps<iArtifact>> = ({
  item,
  access,
  reload,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iArtifact>>({
    shouldUnregister: false,
    defaultValues: { ...item, __access: access },
  })

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item, access])

  const onSubmit = (data: ItemWithAccess<iArtifact>) => {
    setLoading(true)
    handleUpdateData({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iArtifact>>,
      link: "/v1/artifacts",
    })
      .then(() => {
        setLoading(false)
        reload()
        store.addNotification({
          title: "Success!",
          message: "Artifact successfully updated!",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/artifacts/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Artifact successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const createAudio = (data: any) => {
    setLoading(true)

    post<any>({
      link: "/v2/artifact-text-to-speech",
      data: { _id: item._id, data },
    }).then((s) => {
      setLoading(false)
      reload()
    })
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iArtifact>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title={`${item.title ? item.title : "Inspector"} | VR-All-Art`} />
      <ArtifactHeader
        title={item.title || "Inspector"}
        access={access}
        published={!!item.published}
        submitDeleteText={"Delete"}
        submitText="Update"
        isLoading={isLoading}
        handleSubmit={invokeSubmit}
        handleDelete={onDelete}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <div className="inspector">
          <form
            onSubmit={methods.handleSubmit((data) => {
              onSubmit(data as iArtifact)
            })}
            className="inspector-form"
          >
            <Route exact path={match.path}>
              <ArtifactOverview onCreateAudio={createAudio} />
            </Route>
            <Route exact path={match.path + "/details"}>
              <ArtifactDetails />
            </Route>
            <Route exact path={match.path + "/manage-files"}>
              <ArtifactUploads item={item} />
            </Route>
            <button type="submit" className="btn-blue d-none">
              Save Changes
            </button>
          </form>
        </div>
      )}
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iArtifact>()
  const match = useRouteMatch<iUrlParams>()

  const loadArtifact = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { collection: "Artifacts", url: match.params.url },
      }),
      get<iResponse<iArtifact>>({ link: "/v1/artifacts/" + match.params.url }),
    ]).then((result) => {
      const [accessResult, dataResult] = result
      unstable_batchedUpdates(() => {
        setItem(dataResult.data)
        setAccess(accessResult.data)
        setLoading(false)
      })
    })
  }

  useMemo(() => {
    loadArtifact()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <ArtifactInspector item={item} access={access} reload={loadArtifact} />
  ) : null
}

export default InspectorLoader
